import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData, saveDossier } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import SaveLoader from "../shared/SaveLoader";
import useConfidence from "../../hooks/useConfidence";
import Tab1PremiereConsultation from "./tabs/Tab1PremiereConsultation";
import Tab2Antecedents          from "./tabs/Tab2Antecedents";
import Tab3ModeVieFamille       from "./tabs/Tab3ModeVieFamille";
import Tab4HistoireMaladie      from "./tabs/Tab4HistoireMaladie";
import Tab5ExamensPhysiques     from "./tabs/Tab5ExamensPhysiques";
import Tab6ResumeDiagnostic     from "./tabs/Tab6ResumeDiagnostic";
import "./ObservationsMalade.css";
import toast from "react-hot-toast";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";

const RUBRIQUE = "observations_malade";

const TABS_CONFIG = [
  { id: "tab1", label: "1re Consultation", subItems: null },
  {
    id: "tab2",
    label: "Antécédents",
    subItems: [
      { id: "personnels", label: "Personnels" },
      { id: "familiaux",  label: "Familiaux"  },
    ],
  },
  { id: "tab3", label: "Mode Vie Famille", subItems: null },
  { id: "tab4", label: "Histoire Maladie", subItems: null },
  {
    id: "tab5",
    label: "Examens Physiques",
    subItems: [
      { id: "general",           label: "Général" },
    { id: "ophtalmologie", label: "Ophtalmologie" },
    { id: "maxilo_facial", label: "Maxillo-facial" },

      { id: "abdomen_pelvis",    label: "Abdomen & Pelvis" },
      { id: "neurologique",      label: "Neurologique" },
      { id: "aires_ganglion",    label: "Aires ganglionnaires" },
      { id: "locomoteur",        label: "Locomoteur" },
      { id: "cardio_thoracique", label: "Cardio-thoracique" },
      { id: "autres_appareils",  label: "Autres appareils" },
    ],
  },
  { id: "tab6", label: "Résumé & Diagnostic", subItems: null },
];

const INITIAL = {
  tab1: {}, tab2: {}, tab3: {}, tab4: {}, tab5: {},
  tab6: { resume: "", hypothese_1: "", hypothese_2: "", hypothese_3: "", notes_complementaires: "" },
};

const normalizeAutresAppareils = (saved) => {
  if (!saved) return {};
  const details = saved.autres_appareils_details;
  return {
    ...saved,
    autres_appareils_details: Array.isArray(details) ? details : [],
  };
};

const INITIAL_TAB2_PERSONNELS = {
  apgar_one_min:  "",
  apgar_five_min: "",
};

const INITIAL_TAB2_FAMILIAUX = {
  pere_alcoolisme: "non",
  pere_tabagisme:  "non",
  pere_drogues:    "non",
  mere_alcoolisme: "non",
  mere_tabagisme:  "non",
  mere_drogues:    "non",
};

const ObservationsMalade = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();
  const [saving, setSaving] = useState(false);

  const rawImages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];

  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;

  const savedTab1            = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.premiere_consultation     || {});
  const savedTab2Personnels  = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.antecedents_personnels    || {});
  const savedTab2Familiaux   = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.antecedents_familiaux     || {});
  const savedTab3            = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.mode_vie_famille          || {});
  const savedTab4            = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.histoire_maladie          || {});
  const savedTab5General     = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_general           || {});
  const savedTab5Ophtalmo    = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_ophtalmologie     || {});
  const savedTab5Maxilo      = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_maxilo_facial     || {});
  const savedTab5Abdomen     = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_abdomen_pelvis    || {});
  const savedTab5Neuro       = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_neurologique      || {});
  const savedTab5Ganglion    = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_aires_ganglion    || {});
  const savedTab5Locomoteur  = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_locomoteur        || {});
  const savedTab5Cardio      = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_cardio_thoracique || {});
  const savedTab5Autres      = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens_autres_appareils  || {});
  const savedTab6            = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.resume_diagnostic         || {});

  const [tab1,           setTab1]           = useState({ ...INITIAL.tab1, ...savedTab1 });
  const [tab2Personnels, setTab2Personnels] = useState({ ...INITIAL_TAB2_PERSONNELS, ...savedTab2Personnels });
  const [tab2Familiaux, setTab2Familiaux] = useState({ ...INITIAL_TAB2_FAMILIAUX, ...savedTab2Familiaux });
  const [tab3,           setTab3]           = useState({ ...INITIAL.tab3, ...savedTab3 });
  const [tab4,           setTab4]           = useState({ ...INITIAL.tab4, ...savedTab4 });
  const [tab5General,    setTab5General]    = useState({ ...savedTab5General    });
  const [tab5Ophtalmo,   setTab5Ophtalmo]   = useState({ ...savedTab5Ophtalmo   });
  const [tab5Maxilo,     setTab5Maxilo]     = useState({ ...savedTab5Maxilo     });
  const [tab5Abdomen,    setTab5Abdomen]    = useState({ ...savedTab5Abdomen    });
  const [tab5Neuro,      setTab5Neuro]      = useState({ ...savedTab5Neuro      });
  const [tab5Ganglion,   setTab5Ganglion]   = useState({ ...savedTab5Ganglion   });
  const [tab5Locomoteur, setTab5Locomoteur] = useState({ ...savedTab5Locomoteur });
  const [tab5Cardio,     setTab5Cardio]     = useState({ ...savedTab5Cardio     });
  const [tab5Autres,     setTab5Autres]     = useState({ ...savedTab5Autres     });
  const [tab6,           setTab6]           = useState({ ...INITIAL.tab6, ...savedTab6 });

  useEffect(() => {
    setTab1({ ...INITIAL.tab1, ...savedTab1 });
    setTab2Personnels({ ...INITIAL_TAB2_PERSONNELS, ...savedTab2Personnels });
    setTab2Familiaux({ ...INITIAL_TAB2_FAMILIAUX, ...savedTab2Familiaux });
    setTab3({ ...INITIAL.tab3, ...savedTab3 });
    setTab4({ ...INITIAL.tab4, ...savedTab4 });
    setTab5General({    ...savedTab5General    });
    setTab5Ophtalmo({   ...savedTab5Ophtalmo   });
    setTab5Maxilo({     ...savedTab5Maxilo     });
    setTab5Abdomen({    ...savedTab5Abdomen    });
    setTab5Neuro({      ...savedTab5Neuro      });
    setTab5Ganglion({   ...savedTab5Ganglion   });
    setTab5Locomoteur({ ...savedTab5Locomoteur });
    setTab5Cardio({     ...savedTab5Cardio     });
    setTab5Autres(normalizeAutresAppareils(savedTab5Autres));
    setTab6({ ...INITIAL.tab6, ...savedTab6 });
  }, [id]);

  const tab1Ref           = useRef(tab1);
  const tab2PersonnelsRef = useRef(tab2Personnels);
  const tab2FamiliauxRef  = useRef(tab2Familiaux);
  const tab3Ref           = useRef(tab3);
  const tab4Ref           = useRef(tab4);
  const tab5GeneralRef    = useRef(tab5General);
  const tab5OphtalmoRef   = useRef(tab5Ophtalmo);
  const tab5MaxiloRef     = useRef(tab5Maxilo);
  const tab5AbdomenRef    = useRef(tab5Abdomen);
  const tab5NeuroRef      = useRef(tab5Neuro);
  const tab5GanglionRef   = useRef(tab5Ganglion);
  const tab5LocomoteurRef = useRef(tab5Locomoteur);
  const tab5CardioRef     = useRef(tab5Cardio);
  const tab5AutresRef     = useRef(tab5Autres);
  const tab6Ref           = useRef(tab6);

  useEffect(() => { tab1Ref.current           = tab1;           }, [tab1]);
  useEffect(() => { tab2PersonnelsRef.current = tab2Personnels; }, [tab2Personnels]);
  useEffect(() => { tab2FamiliauxRef.current  = tab2Familiaux;  }, [tab2Familiaux]);
  useEffect(() => { tab3Ref.current           = tab3;           }, [tab3]);
  useEffect(() => { tab4Ref.current           = tab4;           }, [tab4]);
  useEffect(() => { tab5GeneralRef.current    = tab5General;    }, [tab5General]);
  useEffect(() => { tab5OphtalmoRef.current   = tab5Ophtalmo;   }, [tab5Ophtalmo]);
  useEffect(() => { tab5MaxiloRef.current     = tab5Maxilo;     }, [tab5Maxilo]);
  useEffect(() => { tab5AbdomenRef.current    = tab5Abdomen;    }, [tab5Abdomen]);
  useEffect(() => { tab5NeuroRef.current      = tab5Neuro;      }, [tab5Neuro]);
  useEffect(() => { tab5GanglionRef.current   = tab5Ganglion;   }, [tab5Ganglion]);
  useEffect(() => { tab5LocomoteurRef.current = tab5Locomoteur; }, [tab5Locomoteur]);
  useEffect(() => { tab5CardioRef.current     = tab5Cardio;     }, [tab5Cardio]);
  useEffect(() => { tab5AutresRef.current     = tab5Autres;     }, [tab5Autres]);
  useEffect(() => { tab6Ref.current           = tab6;           }, [tab6]);

  const [activeTab, setActiveTab] = useState("tab1");
  const [activeSub, setActiveSub] = useState({ tab2: "personnels", tab5: "general" });
  const [imgIndex,  setImgIndex]  = useState(0);

// APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav(RUBRIQUE);
  // ── Confidence — un getConfidence par sous-rubrique ──────────────────────────
  const { getConfidence: gcRoot } = useConfidence(id, RUBRIQUE);
  const makeGC = (sousRubrique) => (field) => gcRoot(sousRubrique, field);

  const gcTab1        = makeGC("premiere_consultation");
  const gcTab2Perso   = makeGC("antecedents_personnels");
  const gcTab2Fam     = makeGC("antecedents_familiaux");
  const gcTab3        = makeGC("mode_vie_famille");
  const gcTab4        = makeGC("histoire_maladie");
  const gcTab5General = makeGC("examens_general");
  const gcTab5Ophtalmo= makeGC("examens_ophtalmologie");
  const gcTab5Maxilo  = makeGC("examens_maxilo_facial");
  const gcTab5Abdomen = makeGC("examens_abdomen_pelvis");
  const gcTab5Neuro   = makeGC("examens_neurologique");
  const gcTab5Ganglion= makeGC("examens_aires_ganglion");
  const gcTab5Locomoteur = makeGC("examens_locomoteur");
  const gcTab5Cardio  = makeGC("examens_cardio_thoracique");
  const gcTab5Autres  = makeGC("examens_autres_appareils");
  const gcTab6        = makeGC("resume_diagnostic");

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
    if (tab.subItems && !activeSub[tab.id]) {
      setActiveSub((prev) => ({ ...prev, [tab.id]: tab.subItems[0].id }));
    }
  };

  const handleSubClick = (tabId, subId) => {
    setActiveTab(tabId);
    setActiveSub((prev) => ({ ...prev, [tabId]: subId }));
  };

  const makeOnChange = (setter) => (field, value) =>
    setter((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, "premiere_consultation",     tab1Ref.current));
    dispatch(updateFormData(id, RUBRIQUE, "antecedents_personnels",    tab2PersonnelsRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "antecedents_familiaux",     tab2FamiliauxRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "mode_vie_famille",          tab3Ref.current));
    dispatch(updateFormData(id, RUBRIQUE, "histoire_maladie",          tab4Ref.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_general",           tab5GeneralRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_ophtalmologie",     tab5OphtalmoRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_maxilo_facial",     tab5MaxiloRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_abdomen_pelvis",    tab5AbdomenRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_neurologique",      tab5NeuroRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_aires_ganglion",    tab5GanglionRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_locomoteur",        tab5LocomoteurRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_cardio_thoracique", tab5CardioRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "examens_autres_appareils",  tab5AutresRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "resume_diagnostic",         tab6Ref.current));
  };

  useEffect(() => {
    return () => { handleSave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    dispatch(updateFormData(id, RUBRIQUE, "premiere_consultation",     tab1));
    dispatch(updateFormData(id, RUBRIQUE, "antecedents_personnels",    tab2Personnels));
    dispatch(updateFormData(id, RUBRIQUE, "antecedents_familiaux",     tab2Familiaux));
    dispatch(updateFormData(id, RUBRIQUE, "mode_vie_famille",          tab3));
    dispatch(updateFormData(id, RUBRIQUE, "histoire_maladie",          tab4));
    dispatch(updateFormData(id, RUBRIQUE, "examens_general",           tab5General));
    dispatch(updateFormData(id, RUBRIQUE, "examens_ophtalmologie",     tab5Ophtalmo));
    dispatch(updateFormData(id, RUBRIQUE, "examens_maxilo_facial",     tab5Maxilo));
    dispatch(updateFormData(id, RUBRIQUE, "examens_abdomen_pelvis",    tab5Abdomen));
    dispatch(updateFormData(id, RUBRIQUE, "examens_neurologique",      tab5Neuro));
    dispatch(updateFormData(id, RUBRIQUE, "examens_aires_ganglion",    tab5Ganglion));
    dispatch(updateFormData(id, RUBRIQUE, "examens_locomoteur",        tab5Locomoteur));
    dispatch(updateFormData(id, RUBRIQUE, "examens_cardio_thoracique", tab5Cardio));
    dispatch(updateFormData(id, RUBRIQUE, "examens_autres_appareils",  tab5Autres));
    dispatch(updateFormData(id, RUBRIQUE, "resume_diagnostic",         tab6));
    try {
      const result = await dispatch(saveDossier());
      toast.success(result.message);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setTab1({ ...INITIAL.tab1, ...savedTab1 });
    setTab2Personnels({ ...INITIAL_TAB2_PERSONNELS, ...savedTab2Personnels });
    setTab2Familiaux({ ...INITIAL_TAB2_FAMILIAUX, ...savedTab2Familiaux });
    setTab3({ ...INITIAL.tab3, ...savedTab3 });
    setTab4({ ...INITIAL.tab4, ...savedTab4 });
    setTab5General({    ...savedTab5General    });
    setTab5Ophtalmo({   ...savedTab5Ophtalmo   });
    setTab5Maxilo({     ...savedTab5Maxilo     });
    setTab5Abdomen({    ...savedTab5Abdomen    });
    setTab5Neuro({      ...savedTab5Neuro      });
    setTab5Ganglion({   ...savedTab5Ganglion   });
    setTab5Locomoteur({ ...savedTab5Locomoteur });
    setTab5Cardio({     ...savedTab5Cardio     });
    setTab5Autres(normalizeAutresAppareils(savedTab5Autres));
    setTab6({ ...INITIAL.tab6, ...savedTab6 });
  };

  if (!dossier) {
    return (
      <div className="om-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="om-page">
      {saving && <SaveLoader />}

      <header className="om-header">
        <div className="om-header-left">
          <button className="om-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="om-title">Dossier {dossier.numero_dossier}</h1>
            <p className="om-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="om-save-btn" onClick={handleSaveAll} disabled={saving}>
          Sauvegarder les modifications
        </button>
      </header>

      <div className="om-body">
        <aside className="om-image-panel">
          <div className="om-image-header">
            <span className="om-rubrique-label">Observation du Malade</span>
            <span className="om-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="om-image-viewer">
            <img src={IMAGES[imgIndex]} alt={`Page ${imgIndex + 1}`} className="om-image" />
          </div>
          <div className="om-image-nav">
            <button className="om-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
            <div className="om-image-dots">
              {IMAGES.map((_, i) => (
                <span key={i} className={`om-dot ${i === imgIndex ? "om-dot--active" : ""}`} onClick={() => setImgIndex(i)} />
              ))}
            </div>
            <button className="om-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
          </div>
        </aside>

        <main className="om-form-panel">
          <nav className="om-tabs">
            {TABS_CONFIG.map((tab) => {
              const isActive = activeTab === tab.id;
              const hasSubItems = !!tab.subItems;
              return (
                <div key={tab.id} className={`om-tab-wrapper ${isActive ? "om-tab-wrapper--active" : ""}`}>
                  <button
                    className={`om-tab ${isActive ? "om-tab--active" : ""}`}
                    onClick={() => handleTabClick(tab)}
                  >
                    {tab.label}
                    {hasSubItems && <span className="om-tab-chevron">▾</span>}
                  </button>
                  {hasSubItems && (
                    <div className="om-dropdown">
                      {tab.subItems.map((sub) => (
                        <button key={sub.id}
                          className={`om-dropdown-item ${activeSub[tab.id] === sub.id && isActive ? "om-dropdown-item--active" : ""}`}
                          onClick={() => handleSubClick(tab.id, sub.id)}>
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="om-tab-content">
            {(() => {
              const currentTab = TABS_CONFIG.find((t) => t.id === activeTab);
              const subLabel = currentTab?.subItems
                ? currentTab.subItems.find((s) => s.id === activeSub[activeTab])?.label
                : null;
              return (
                <div className="om-content-title">
                  {subLabel ? (
                    <>
                      <span className="om-content-title__parent">{currentTab.label}</span>
                      <span className="om-content-title__sep"> › </span>
                      <span className="om-content-title__child">{subLabel}</span>
                    </>
                  ) : (
                    <span className="om-content-title__child">{currentTab?.label}</span>
                  )}
                </div>
              );
            })()}

            {activeTab === "tab1" && (
              <Tab1PremiereConsultation form={tab1} onChange={makeOnChange(setTab1)} getConfidence={gcTab1} />
            )}

            {activeTab === "tab2" && activeSub.tab2 === "personnels" && (
              <Tab2Antecedents form={tab2Personnels} onChange={makeOnChange(setTab2Personnels)} activeSub={activeSub.tab2} getConfidence={gcTab2Perso} />
            )}
            {activeTab === "tab2" && activeSub.tab2 === "familiaux" && (
              <Tab2Antecedents form={tab2Familiaux}  onChange={makeOnChange(setTab2Familiaux)}  activeSub={activeSub.tab2} getConfidence={gcTab2Fam} />
            )}

            {activeTab === "tab3" && (
              <Tab3ModeVieFamille form={tab3} onChange={makeOnChange(setTab3)} getConfidence={gcTab3} />
            )}
            {activeTab === "tab4" && (
              <Tab4HistoireMaladie form={tab4} onChange={makeOnChange(setTab4)} getConfidence={gcTab4} />
            )}

            {activeTab === "tab5" && activeSub.tab5 === "general"           && <Tab5ExamensPhysiques form={tab5General}    onChange={makeOnChange(setTab5General)}    activeSub={activeSub.tab5} getConfidence={gcTab5General} />}

            {activeTab === "tab5" && activeSub.tab5 === "ophtalmologie" && <Tab5ExamensPhysiques form={tab5Ophtalmo} onChange={makeOnChange(setTab5Ophtalmo)} activeSub={activeSub.tab5} getConfidence={gcTab5Ophtalmo} />}
            {activeTab === "tab5" && activeSub.tab5 === "maxilo_facial" && <Tab5ExamensPhysiques form={tab5Maxilo} onChange={makeOnChange(setTab5Maxilo)} activeSub={activeSub.tab5} getConfidence={gcTab5Maxilo} />}
            {activeTab === "tab5" && activeSub.tab5 === "abdomen_pelvis"    && <Tab5ExamensPhysiques form={tab5Abdomen}    onChange={makeOnChange(setTab5Abdomen)}    activeSub={activeSub.tab5} getConfidence={gcTab5Abdomen} />}
            {activeTab === "tab5" && activeSub.tab5 === "neurologique"      && <Tab5ExamensPhysiques form={tab5Neuro}      onChange={makeOnChange(setTab5Neuro)}      activeSub={activeSub.tab5} getConfidence={gcTab5Neuro} />}
            {activeTab === "tab5" && activeSub.tab5 === "aires_ganglion"    && <Tab5ExamensPhysiques form={tab5Ganglion}   onChange={makeOnChange(setTab5Ganglion)}   activeSub={activeSub.tab5} getConfidence={gcTab5Ganglion} />}
            {activeTab === "tab5" && activeSub.tab5 === "locomoteur"        && <Tab5ExamensPhysiques form={tab5Locomoteur} onChange={makeOnChange(setTab5Locomoteur)} activeSub={activeSub.tab5} getConfidence={gcTab5Locomoteur} />}
            {activeTab === "tab5" && activeSub.tab5 === "cardio_thoracique" && <Tab5ExamensPhysiques form={tab5Cardio}     onChange={makeOnChange(setTab5Cardio)}     activeSub={activeSub.tab5} getConfidence={gcTab5Cardio} />}
            {activeTab === "tab5" && activeSub.tab5 === "autres_appareils"  && <Tab5ExamensPhysiques form={tab5Autres}     onChange={makeOnChange(setTab5Autres)}     activeSub={activeSub.tab5} getConfidence={gcTab5Autres} />}

            {activeTab === "tab6" && (
              <Tab6ResumeDiagnostic form={tab6} onChange={makeOnChange(setTab6)} getConfidence={gcTab6} />
            )}
          </div>

          {/* <div className="om-form-actions">
            <button className="om-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="om-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}


<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />        </main>
      </div>
    </div>
  );
};

export default ObservationsMalade;